import { useMutation } from '@tanstack/react-query';
import { BIS_BOARD_FILES_MAX, type BisBoardFamily } from '@raro/domain';
import { Button, Input, Label, Notice, Thinking, Toggle } from '@raro/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { waitProgress } from '../ResearchLive';
import { useBisAssets } from './assets';

type Read = { summary: string; notes: string; families: BisBoardFamily[] };
type Pick = { on: boolean; title: string; whole: boolean; crop: string | null };
/** folga em volta da caixa que a Machine marcou: a caixa é aproximada, e cortar um desenho no meio estraga a prancha */
const PAD = 0.02;

/** o recorte da família na imagem que a pessoa subiu (o arquivo local: sem ir buscar de volta no servidor) */
async function crop(file: File, box: [number, number, number, number]): Promise<Blob> {
  const img = await createImageBitmap(file);
  const x0 = Math.max(0, box[0] - PAD), y0 = Math.max(0, box[1] - PAD), x1 = Math.min(1, box[2] + PAD), y1 = Math.min(1, box[3] + PAD);
  const w = Math.round((x1 - x0) * img.width), h = Math.round((y1 - y0) * img.height);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d')!.drawImage(img, Math.round(x0 * img.width), Math.round(y0 * img.height), w, h, 0, 0, w, h);
  return new Promise((ok, fail) => canvas.toBlob((b) => (b ? ok(b) : fail(new Error('não consegui recortar a prancha'))), 'image/png'));
}

/**
 * LER PRANCHAS no capítulo visual: a pessoa sobe a prancha, a Machine separa as famílias (ícones e ilustrações) e preenche
 * cada bloco com o que vê. Aqui a pessoa confere o recorte de cada família, o nome e o que entra; ao criar, o recorte (ou a
 * prancha inteira) sobe como arquivo da marca e vira a prancha do bloco, que entra como PROPOSTA para aceitar na lista.
 */
export function BoardReader({ organizationId, brandId, onDone, onClose }: { organizationId: string; brandId: string | null; onDone: () => void; onClose: () => void }) {
  const { upload } = useBisAssets();
  const picker = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [read, setRead] = useState<Read | null>(null);
  const [picks, setPicks] = useState<Pick[]>([]);
  // a imagem inteira de cada arquivo, para a prévia de quem não usa recorte
  const whole = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => whole.forEach((u) => URL.revokeObjectURL(u)), [whole]);
  const run = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      const progressId = crypto.randomUUID();
      form.append('progressId', progressId);
      if (brandId) form.append('brandId', brandId);
      for (const f of files) form.append('files', f);
      await api.post(`/organizations/${organizationId}/bis/boards/read`, form);
      return waitProgress<Read>(progressId, { what: 'a leitura da prancha', maxMinutes: 4 });
    },
    onSuccess: (r) => { setRead(r); setPicks(r.families.map((f) => ({ on: true, title: f.title, whole: !f.box, crop: null }))); },
  });
  // a prévia de cada recorte, feita uma vez quando a leitura chega
  useEffect(() => {
    if (!read) return;
    let stop = false;
    void Promise.all(read.families.map(async (f) => (f.box && files[f.image] ? URL.createObjectURL(await crop(files[f.image]!, f.box)) : null))).then((urls) => {
      if (stop) { urls.forEach((u) => u && URL.revokeObjectURL(u)); return; }
      setPicks((p) => p.map((x, i) => ({ ...x, crop: urls[i] ?? null })));
    }).catch(() => undefined);
    return () => { stop = true; };
  }, [read, files]);
  const create = useMutation({
    mutationFn: async () => {
      if (!read || !upload) throw new Error('sem permissão para subir arquivo no BIS');
      const blocks = [];
      for (const [i, f] of read.families.entries()) {
        const p = picks[i]!;
        if (!p.on) continue;
        const source = files[f.image]!;
        const blob = !p.whole && f.box ? await crop(source, f.box) : source;
        const name = `${p.title.trim() || f.title} (prancha).png`;
        const ref = await upload(new File([blob], name, { type: blob.type || 'image/png' }), f.kind === 'icons' ? 'icon' : 'illustration');
        blocks.push({ kind: f.kind, title: p.title.trim() || f.title, body: f.body, data: { ...f.data, reference: [{ assetId: ref.assetId, label: 'prancha' }] } });
      }
      if (!blocks.length) throw new Error('marque ao menos uma família');
      return api.post<{ ids: string[] }>(`/organizations/${organizationId}/bis/boards/apply`, { brandId: brandId ?? undefined, blocks });
    },
    onSuccess: () => { onDone(); onClose(); },
  });
  const chosen = picks.filter((p) => p.on).length;

  return (
    <div className="rr-bisbuild__box rr-bisboard">
      <div className="rr-bisboard__head">
        <Label>ler pranchas</Label>
        <Button size="sm" variant="quiet" onClick={onClose}>fechar</Button>
      </div>
      {!read && (
        <>
          <small>suba a prancha de ícones, de ilustrações ou as duas numa imagem só (PNG, JPG ou WebP, até {BIS_BOARD_FILES_MAX}). a Machine separa as famílias, preenche cada bloco com o que vê e diz o que não dá para ler na imagem. os blocos entram como proposta.</small>
          <input ref={picker} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={(e) => { setFiles([...(e.target.files ?? [])].slice(0, BIS_BOARD_FILES_MAX)); e.target.value = ''; }} />
          <div className="rr-bisboard__row">
            <Button size="sm" variant="quiet" disabled={run.isPending} onClick={() => picker.current?.click()}>{files.length ? 'trocar imagens' : 'escolher imagens'}</Button>
            {files.length > 0 && <small>{files.map((f) => f.name).join(', ')}</small>}
          </div>
          {run.isPending ? <Thinking label="lendo a prancha" steps={[{ kind: 'stage', text: 'separando as famílias e lendo o traço de cada uma' }]} lines={2} />
            : <Button size="sm" variant="primary" disabled={!files.length} onClick={() => run.mutate()}>ler com a Machine</Button>}
          {run.error && <Notice tone="error">{(run.error as Error).message}</Notice>}
        </>
      )}
      {read && (
        <>
          <small>{read.summary}</small>
          {read.notes && <small className="rr-faint">{read.notes}</small>}
          {!read.families.length && <Notice>a Machine não achou família de ícones nem de ilustrações nesta imagem.</Notice>}
          {read.families.map((f, i) => {
            const p = picks[i];
            if (!p) return null;
            const set = (x: Partial<Pick>) => setPicks((all) => all.map((y, j) => (j === i ? { ...y, ...x } : y)));
            return (
              <article key={i} className="rr-bisboard__family">
                <div className="rr-bisboard__preview">
                  {!p.whole && p.crop ? <img src={p.crop} alt={`recorte de ${p.title}`} /> : whole[f.image] ? <img src={whole[f.image]} alt="a prancha inteira" /> : null}
                </div>
                <div className="rr-bisboard__fields">
                  <Toggle checked={p.on} onChange={(on) => set({ on })} label={f.kind === 'icons' ? 'família de ícones' : 'família de ilustrações'} hint={f.examples.length ? f.examples.join(', ') : undefined} />
                  <Input value={p.title} onChange={(e) => set({ title: e.target.value })} disabled={!p.on} aria-label="nome da família" />
                  <small>{f.body}</small>
                  {typeof f.data.style === 'string' && <small className="rr-faint">{f.data.style}</small>}
                  {f.missing && <small className="rr-faint">não dá para ler na imagem: {f.missing}</small>}
                  {f.box && <Toggle checked={p.whole} onChange={(whole) => set({ whole })} disabled={!p.on} label="usar a prancha inteira" hint="sem o recorte: a Machine olha a imagem toda ao desenhar esta família" />}
                </div>
              </article>
            );
          })}
          <div className="rr-bisboard__row">
            {read.families.length > 0 && <Button size="sm" variant="primary" disabled={!chosen || create.isPending} onClick={() => create.mutate()}>{create.isPending ? 'criando…' : chosen === 1 ? 'criar o bloco proposto' : `criar ${chosen} blocos propostos`}</Button>}
            <Button size="sm" variant="quiet" disabled={create.isPending} onClick={() => { setRead(null); setPicks([]); run.reset(); }}>ler outra</Button>
          </div>
          {create.error && <Notice tone="error">{(create.error as Error).message}</Notice>}
        </>
      )}
    </div>
  );
}
